import {gql, useMutation, useQuery} from '@apollo/client';
import {captureException} from '@sentry/react';
import {useSnackbar} from 'notistack';
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import withAutoFormNew from '../../components/AutoForm/AutoFormNew';
import Loading from '../../components/Loading/Loading';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {ADDRESS_FRAGMENT, LEAD_DETAILS_FRAGMENT} from '../../graphql/fragments';
import {CreateLead, CreateLeadVariables} from '../../schema-types/CreateLead';
import {
  CreateLeadAddress,
  CreateLeadAddressVariables,
} from '../../schema-types/CreateLeadAddress';
import {LeadDetailsFragment} from '../../schema-types/LeadDetailsFragment';
import {LeadQuery, LeadQueryVariables} from '../../schema-types/LeadQuery';
import {SubmitLead, SubmitLeadVariables} from '../../schema-types/SubmitLead';
import {UpdateLead, UpdateLeadVariables} from '../../schema-types/UpdateLead';
import {
  UpdateLeadAddress,
  UpdateLeadAddressVariables,
} from '../../schema-types/UpdateLeadAddress';

import {LeadFormValues} from './LeadBasic';
import {leadStep, LeadStepper} from './LeadStepper';
import validateUUID from '../../utils/validateUUID';

export const LeadContext = React.createContext<{
  lead: LeadDetailsFragment | null;
  goToStep: (step: leadStep) => void;
  currentStep: leadStep;
  onCreateOrUpdate: (updateVars: LeadFormValues) => Promise<string>;
  onCreateOrUpdateLoading: boolean;
  leadId: string;
  saving: boolean;
  refetchLead: () => void;
}>({
  lead: null,
  goToStep: () => {},
  currentStep: 'basic',
  onCreateOrUpdate: async () => '',
  onCreateOrUpdateLoading: false,
  leadId: '',
  saving: false,
  refetchLead: () => {},
});

export const useLeadContext = () => {
  const context = useContext(LeadContext);
  return context;
};

const leadQuery = gql`
  query LeadQuery($leadId: UUID!) {
    leadDetails(leadId: $leadId) {
      ...LeadDetailsFragment
    }
  }
  ${LEAD_DETAILS_FRAGMENT}
`;

export const createLeadAddressMutation = gql`
  mutation CreateLeadAddress($leadId: UUID!, $isPrimary: Boolean!) {
    createAddress(leadId: $leadId, isPrimary: $isPrimary) {
      address {
        id
      }
      errors
    }
  }
`;

export const updateLeadAddressMutation = gql`
  mutation UpdateLeadAddress(
    $addressId: UUID!
    $city: String
    $composite: String
    $country: String
    $county: String
    $isPrimary: Boolean!
    $leadId: UUID!
    $line1: String
    $line2: String
    $line3: String
    $postcode: String
  ) {
    updateAddress(
      addressId: $addressId
      city: $city
      composite: $composite
      country: $country
      county: $county
      isPrimary: $isPrimary
      leadId: $leadId
      line1: $line1
      line2: $line2
      line3: $line3
      postcode: $postcode
    ) {
      ok
      address {
        ...AddressFragment
      }
    }
  }
  ${ADDRESS_FRAGMENT}
`;

export const updateLeadMutation = gql`
  mutation UpdateLead(
    $accountId: UUID
    $accountName: String
    $accountType: String
    $adviserId: UUID
    $budgetAmount: Decimal
    $companyName: String
    $contactId: UUID
    $dob: DateTime
    $firstName: String
    $homePhone: String
    $initialFixedFee: Decimal
    $initialPercentageFee: Decimal
    $jobTitle: String
    $knownAs: String
    $lastName: String
    $middleName: String
    $leadId: UUID!
    $leadSource: String
    $linkedinProfile: String
    $mobilePhone: String
    $nationalInsurance: String
    $nationality: String
    $ongoingFee: Decimal
    $primaryEmail: String
    $relatedLeadId: UUID
    $secondaryEmail: String
    $subStatus: String
    $title: String
    $residency: String
    $domicile: String
  ) {
    updateLead(
      accountId: $accountId
      accountName: $accountName
      accountType: $accountType
      adviserId: $adviserId
      budgetAmount: $budgetAmount
      companyName: $companyName
      contactId: $contactId
      dob: $dob
      firstName: $firstName
      middleName: $middleName
      homePhone: $homePhone
      initialFixedFee: $initialFixedFee
      initialPercentageFee: $initialPercentageFee
      jobTitle: $jobTitle
      knownAs: $knownAs
      lastName: $lastName
      leadId: $leadId
      leadSource: $leadSource
      linkedinProfile: $linkedinProfile
      mobilePhone: $mobilePhone
      nationalInsurance: $nationalInsurance
      residency: $residency
      domicile: $domicile
      nationality: $nationality
      ongoingFee: $ongoingFee
      primaryEmail: $primaryEmail
      relatedLeadId: $relatedLeadId
      secondaryEmail: $secondaryEmail
      subStatus: $subStatus
      title: $title
    ) {
      lead {
        ...LeadDetailsFragment
      }
    }
  }
  ${LEAD_DETAILS_FRAGMENT}
`;
export const createLeadMutation = gql`
  mutation CreateLead($firstName: String!, $lastName: String!, $middleName: String!) {
    createLead(firstName: $firstName, lastName: $lastName, middleName: $middleName) {
      lead {
        ...LeadDetailsFragment
      }
    }
  }
  ${LEAD_DETAILS_FRAGMENT}
`;

// For internal leads only
const submitLeadMutation = gql`
  mutation SubmitLead($leadId: UUID!) {
    submitLead(leadId: $leadId, convert: true) {
      ok
      lead {
        ...LeadDetailsFragment
      }
      errors
    }
  }
  ${LEAD_DETAILS_FRAGMENT}
`;

const LeadFormDiv: React.FC<PropsWithChildren> = ({children}) => {
  return <>{children}</>;
};

const AddLeadFormWrapped = withAutoFormNew<LeadFormValues, PropsWithChildren>(
  LeadFormDiv
);

export const LeadFormContextProvider: React.FC<PropsWithChildren> = ({children}) => {
  const {leadId} = useParams<{leadId?: string}>();
  // const {leadId} = useParams<{leadId?: string}>();
  const [step, setStep] = useState<leadStep>('basic');
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();
  const {businessUnit} = useDjangoContext();
  const {
    data,
    loading,
    refetch: refetchLead,
  } = useQuery<LeadQuery, LeadQueryVariables>(leadQuery, {
    variables: {
      leadId: leadId!,
    },
    skip: !leadId,
    fetchPolicy: 'network-only',
  });
  const [onCreateAddressMutation, {error: createError}] = useMutation<
    CreateLeadAddress,
    CreateLeadAddressVariables
  >(createLeadAddressMutation);
  const [onUpdateAddressMutation, {error: updateError}] = useMutation<
    UpdateLeadAddress,
    UpdateLeadAddressVariables
  >(updateLeadAddressMutation);
  const [onCreateLeadMutation, {error: createLeadError}] = useMutation<
    CreateLead,
    CreateLeadVariables
  >(createLeadMutation);
  const [onUpdateLeadMutation, {error: updateLeadError}] = useMutation<
    UpdateLead,
    UpdateLeadVariables
  >(updateLeadMutation);
  const [onSubmitLeadMutation, {error: submitLeadError}] = useMutation<
    SubmitLead,
    SubmitLeadVariables
  >(submitLeadMutation);

  const onCreateOrUpdate = useCallback(
    async function onCreateOrUpdate(updateVars: LeadFormValues): Promise<string> {
      setSaving(true);
      try {
        let newOrExistingLeadId;

        if (!leadId) {
          const {data: createData} = await onCreateLeadMutation({
            variables: {
              firstName: updateVars.primary.firstName,
              lastName: updateVars.primary.lastName,
              middleName: updateVars.primary.middleName,
            },
          });
          if (createData?.createLead?.lead?.id) {
            newOrExistingLeadId = createData?.createLead?.lead.id;
            const newUrl = `/leads/${newOrExistingLeadId}/basic`;
            // used to add leadId to history for back button functionality
            window.history.pushState('', document.title, newUrl);
          } else {
            enqueueSnackbar('Unable to create lead', {
              variant: 'error',
              autoHideDuration: null,
            });
            return '';
          }
        } else {
          newOrExistingLeadId = leadId;
        }
        // eslint-disable-next-line consistent-return
        const {data: updateData} = await onUpdateLeadMutation({
          variables: {
            leadId: newOrExistingLeadId,
            accountId: validateUUID(updateVars.accountId),
            accountName: updateVars.accountName,
            accountType: updateVars.accountType,
            adviserId: validateUUID(updateVars.adviserId),
            companyName: updateVars.companyName,
            contactId: validateUUID(updateVars.primary.contactId),
            dob: updateVars.primary.dob,
            firstName: updateVars.primary.firstName,
            homePhone: updateVars.primary.homePhone,
            jobTitle: updateVars.primary.jobTitle,
            lastName: updateVars.primary.lastName,
            // leadSource: updateVars.primary.leadSource,
            linkedinProfile: updateVars.primary.linkedinProfile,
            mobilePhone: updateVars.primary.mobilePhone,
            nationalInsurance: updateVars.primary.nationalInsurance,
            primaryEmail: updateVars.primary.primaryEmail,
            // relatedLeadId: updateVars.primary.relatedLeadId,
            secondaryEmail: updateVars.primary.secondaryEmail,
            title: updateVars.primary.title,
          },
        });
        let leadAddressId = null;
        if (
          updateData?.updateLead?.lead?.addresses.length &&
          updateData?.updateLead?.lead?.addresses[0].id
        ) {
          // if an address, use that
          leadAddressId = updateData?.updateLead?.lead?.addresses[0].id;
        } else {
          // if no address, then create an address
          const {data: createLeadData} = await onCreateAddressMutation({
            variables: {
              leadId: newOrExistingLeadId,
              isPrimary: true,
            },
          });
          if (!createLeadData?.createAddress?.address?.id) {
            throw Error('unable to create lead adress');
          }
          leadAddressId = createLeadData?.createAddress?.address.id;
        }
        await onUpdateAddressMutation({
          variables: {
            leadId: newOrExistingLeadId,
            addressId: leadAddressId,
            isPrimary: true,
            city: updateVars.city,
            line1: updateVars.line1,
            line2: updateVars.line2,
            line3: updateVars.line3,
            country: updateVars.country,
            county: updateVars.county,
            postcode: updateVars.postcode,
          },
        });
        if (updateData?.updateLead?.lead?.id) {
          return updateData.updateLead.lead.id;
        }
        throw Error();
      } catch (err) {
        captureException(err);
      }
      return '';
    },
    [onCreateLeadMutation, onUpdateLeadMutation, leadId]
  );

  const onSubmitLead = useCallback(async function onSubmitLead(
    updateVars: LeadFormValues
  ): Promise<any> {
    try {
      setSaving(true);
      const id = await onCreateOrUpdate(updateVars);
      if (id) {
        const {data: submitData} = await onSubmitLeadMutation({
          variables: {
            leadId: id,
          },
        });

        if (submitData?.submitLead?.ok) {
          navigate(`/leads/${leadId}/setup-account`);
          return;
        }
        enqueueSnackbar('Unable to submit lead', {variant: 'error'});
      }
    } finally {
      setSaving(false);
    }
  },
  []);

  const initialValueOverrides: Partial<LeadFormValues> = useMemo(() => {
    const lead = data?.leadDetails;
    const firstAddress = lead?.addresses.length ? lead.addresses[0] : null;
    const returnVal: Partial<LeadFormValues> = {
      accountId: lead?.account?.id || undefined,
      accountName: lead?.accountName || undefined,
      accountType: lead?.accountType || 'Household',

      companyName: lead?.companyName || undefined,

      // dob: lead?.dob || undefined,
      // firstName: lead?.firstName || undefined,
      // lastName: lead?.lastName || undefined,
      // // leadSource: lead?.leadSource || undefined,
      // jobTitle: lead?.jobTitle || undefined,
      // // adviserId: lead?.adviserId || undefined,
      // homePhone: lead?.homePhone || undefined,
      // mobilePhone: lead?.mobilePhone || undefined,
      // leadId: lead?.id || undefined,
      // linkedinProfile: lead?.linkedinProfile || undefined,
      // nationalInsurance: lead?.nationalInsurance || undefined,
      // primaryEmail: lead?.primaryEmail || undefined,
      // secondaryEmail: lead?.secondaryEmail || undefined,
      // title: lead?.title || undefined,
      primary: {
        dob: lead?.dob || undefined,
        firstName: lead?.firstName || '',
        lastName: lead?.lastName || '',
        middleName: lead?.lastName || '',
        // leadSource: lead?.leadSource || '',
        jobTitle: lead?.jobTitle || '',
        // adviserId: lead?.adviserId || '',
        homePhone: lead?.homePhone || '',
        mobilePhone: lead?.mobilePhone || '',
        contactError: '',
        linkedinProfile: lead?.linkedinProfile || '',
        nationalInsurance: lead?.nationalInsurance || '',
        primaryEmail: lead?.primaryEmail || '',
        secondaryEmail: lead?.secondaryEmail || '',
        title: lead?.title || '',
        contactId: lead?.contact?.id || '',
      },

      line1: firstAddress?.line1 || '',
      line2: firstAddress?.line2 || '',
      line3: firstAddress?.line3 || '',
      county: firstAddress?.county || '',
      // moveInDate: firstAddress,
      postcode: firstAddress?.postcode || '',
      city: firstAddress?.city || '',
      // addresses
      // addresses: [
      //   {
      //     address1: '',
      //     address2: '',
      //     address3: '',
      //     county: '',
      //     moveInDate: new Date(),
      //     postcode: '',
      //     city: '',
      //   },
      // ],
    };
    return returnVal;
  }, [data?.leadDetails]);

  const contextValue = useMemo(() => {
    return {
      lead: data?.leadDetails || null,
      currentStep: step,
      goToStep: setStep,
      onCreateOrUpdate: async (vars: LeadFormValues) => {
        const result = await onCreateOrUpdate(vars);
        setSaving(false);
        return result;
      },
      onCreateOrUpdateLoading: false,
      leadId: leadId || '',
      saving,
      refetchLead: () => {
        refetchLead();
      },
    };
  }, [data, step, setStep, onCreateOrUpdate, leadId, saving, refetchLead]);

  if (leadId && loading) {
    return <Loading />;
  }

  return (
    <AddLeadFormWrapped
      submit={(values) => {
        onSubmitLead(values);
      }}
      formFields={[]}
      initialValueOverrides={initialValueOverrides}
    >
      <LeadContext.Provider value={contextValue}>
        <LeadStepper currentStep={step} />
        {children}
      </LeadContext.Provider>
    </AddLeadFormWrapped>
  );
};
